import { z } from 'zod/v4'
import {
  PasswordConfirmationSchema,
  PhoneNumberSchema,
  StrongPasswordSchema,
} from './base'
import { PAYMENT_TERM } from '@/utils/constant'

export const AuthLoginFormField = z.object({
  email: z.email({ message: 'Please enter a valid email address.' }),
  password: z.string().min(8),
  rememberMe: z.boolean(),
})

const OrganizationAttributesSchema = z.object({
  name: z
    .string()
    .min(1, 'Company name is required')
    .max(100, 'Company name must be at most 100 characters'),
  registration_no: z
    .string()
    .min(1, 'UEN / GST Reg No. is required')
    .min(9, 'UEN / GST Reg No. must be at least 9 characters')
    .max(10, 'UEN / GST Reg No. must be at most 10 characters'),
  country: z.string().min(1, 'Country is required'),
  business_address: z.string().min(1, 'Business address is required'),
  postal_code: z
    .string()
    .min(1, 'Postal code is required')
    .regex(/^[0-9]{5,6}$/, 'Postal code must be 5–6 digits and numeric'),
  website: z
    .string()
    .optional()
    .transform((val) => {
      if (!val || val.trim() === '') return undefined
      return val.startsWith('http') ? val : `https://${val}`
    })
    .refine(
      (val) => {
        if (!val) return true
        try {
          const url = new URL(val)
          return url.hostname.includes('.') && !url.hostname.endsWith('.')
        } catch {
          return false
        }
      },
      {
        message:
          'Invalid website URL (must include a valid domain like example.com)',
        path: ['website'],
      },
    ),
  payment_term: z.enum(PAYMENT_TERM),
})

const OrganizationAdminSchema = z
  .object({
    first_name: z.string().nonempty('First name is required'),
    last_name: z.string().nonempty('Last name is required'),
    email: z.email('Invalid email address').nonempty('Email is required'),
    phone_number: PhoneNumberSchema,
    tnc: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the terms and conditions',
    }),
    corporate_client: z.boolean().refine((val) => val === true, {
      message: 'You must confirm you are a corporate client',
    }),
    password: StrongPasswordSchema,
    password_confirmation: PasswordConfirmationSchema,
    organization_attributes: OrganizationAttributesSchema,
  })
  .refine((data) => data.password === data.password_confirmation, {
    path: ['password_confirmation'],
    message: 'The password you entered doesn’t match',
  })

export const EditPersonalInformationFormSchema = z.object({
  ...OrganizationAdminSchema.pick({
    first_name: true,
    last_name: true,
    phone_number: true,
  }).shape,
  organization_attributes: z.object({
    ...OrganizationAttributesSchema.omit({ payment_term: true }).shape,
    id: z.string().nonempty(),
  }),
})
export type EditPersonalInformationFormSchema = z.infer<
  typeof EditPersonalInformationFormSchema
>

export const RegisterFormFieldSchema = z.object({
  organization_admin: OrganizationAdminSchema,
})
export type RegisterFormFieldSchema = z.infer<typeof RegisterFormFieldSchema>

const ResetPasswordOrganizationAdmin = OrganizationAdminSchema.pick({
  password: true,
  password_confirmation: true,
})
type ResetPasswordOrganizationAdmin = z.infer<
  typeof ResetPasswordOrganizationAdmin
>

export const ResetPasswordResetPayload = z.object({
  organization_admin: ResetPasswordOrganizationAdmin,
})
export type ResetPasswordResetPayload = z.infer<
  typeof ResetPasswordResetPayload
>
